// ============================================================
// Aritmética de faixa IPv4 para a apresentação.
//
// Mora no frontend de propósito: netmask, broadcast, primeiro/último IP e
// contagem são função PURA do CIDR — não cruzam a faixa com nada. Trafegá-las
// no payload seria mandar cinco campos deriváveis por linha em 223 linhas.
//
// O que depende de cruzamento (projeto, ambiente, estado, registro) vem do
// backend e NÃO é recalculado aqui: duas cópias da regra divergiriam como número
// errado na tela, não como teste vermelho.
// ============================================================

export interface FaixaCalculada {
  netmask: string
  broadcast: string
  first: string
  last: string
  /** Total de endereços do bloco, inclusive rede e broadcast. */
  count: number
  /** Terceiro octeto do endereço de rede — o rótulo do slot no mapa. */
  terceiroOcteto: number
  /** Prefixo do bloco /16 que contém a faixa, ex. `10.20.0.0/16`. */
  bloco16: string
  prefixo: number
}

function paraInteiro(ip: string): number {
  const partes = ip.split('.').map((p) => Number(p))
  if (partes.length !== 4 || partes.some((p) => !Number.isInteger(p) || p < 0 || p > 255)) {
    return Number.NaN
  }
  // `>>> 0` para o resultado ser inteiro sem sinal: 10.x cabe em 31 bits, mas
  // 172.31/172.x e o espaco publico (200.x) estouram para negativo sem isso.
  return ((partes[0] << 24) | (partes[1] << 16) | (partes[2] << 8) | partes[3]) >>> 0
}

function paraTexto(n: number): string {
  return [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.')
}

const VAZIO: FaixaCalculada = {
  netmask: '—',
  broadcast: '—',
  first: '—',
  last: '—',
  count: 0,
  terceiroOcteto: 0,
  bloco16: '—',
  prefixo: 0,
}

/**
 * Calcula os campos derivados de um CIDR IPv4.
 *
 * CIDR malformado devolve travessões em vez de lançar: um dado ruim numa linha
 * não pode derrubar a tabela inteira, e a tela mostrando "—" é diagnóstico
 * melhor que uma página branca.
 */
export function calcularFaixa(cidr: string): FaixaCalculada {
  const [base, prefixoTexto] = String(cidr || '').split('/')
  const prefixo = Number(prefixoTexto)
  const inteiro = paraInteiro(base)
  if (!Number.isFinite(inteiro) || !Number.isInteger(prefixo) || prefixo < 0 || prefixo > 32) {
    return VAZIO
  }

  const mascara = prefixo === 0 ? 0 : (0xffffffff << (32 - prefixo)) >>> 0
  const rede = (inteiro & mascara) >>> 0
  const broadcast = (rede | (~mascara >>> 0)) >>> 0
  const total = 2 ** (32 - prefixo)
  // /31 e /32 não têm faixa útil entre rede e broadcast.
  const temUtil = total > 2

  return {
    netmask: paraTexto(mascara),
    broadcast: paraTexto(broadcast),
    first: temUtil ? paraTexto(rede + 1) : paraTexto(rede),
    last: temUtil ? paraTexto(broadcast - 1) : paraTexto(broadcast),
    count: total,
    terceiroOcteto: (rede >>> 8) & 255,
    bloco16: `${paraTexto((rede & 0xffff0000) >>> 0)}/16`,
    prefixo,
  }
}

/** Duas faixas se cruzam? Usado só para pintar colisão no mapa. */
export function sobrepoe(a: string, b: string): boolean {
  const fa = calcularFaixa(a)
  const fb = calcularFaixa(b)
  if (!fa.count || !fb.count) return false
  const inicioA = paraInteiro(a.split('/')[0]) & ((0xffffffff << (32 - fa.prefixo)) >>> 0)
  const inicioB = paraInteiro(b.split('/')[0]) & ((0xffffffff << (32 - fb.prefixo)) >>> 0)
  return inicioA < inicioB + fb.count && inicioB < inicioA + fa.count
}

/** Formata contagem no padrão pt-BR (223 → "223", 65536 → "65.536"). */
export function formatarNumero(n: number): string {
  return Number(n).toLocaleString('pt-BR')
}
